import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/es';
moment.locale('es');

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatChileHHmm = (isoOrDate) => {
  try {
    if (!isoOrDate) return '';
    const s = typeof isoOrDate === 'string' ? isoOrDate : '';
    const hasTz = s && /[zZ]|[+-]\d{2}:?\d{2}$/.test(s);
    const d = new Date(s && !hasTz ? `${s}Z` : isoOrDate);
    return new Intl.DateTimeFormat('es-CL', {
      timeZone: 'America/Santiago',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    return '';
  }
};

const StateBadge = ({ name, color }) => {
  if (!name) return null;
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: color || '#666' }}>
      {name}
    </span>
  );
};

const PayBadge = ({ estado }) => {
  if (!estado) return null;
  const map = {
    'Pagado Completo': { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'PAGADO' },
    'Abono': { bg: 'bg-amber-100', text: 'text-amber-700', label: 'ABONO' },
    'Pendiente': { bg: 'bg-red-100', text: 'text-red-700', label: 'PENDIENTE' },
  };
  const s = map[estado] || { bg: 'bg-gray-100', text: 'text-gray-600', label: estado };
  return (
    <span className={`${s.bg} ${s.text} px-2 py-0.5 rounded-full text-xs font-semibold`}>
      {s.label}
    </span>
  );
};

const SurgeryCard = ({ s, showAll, doctorId }) => {
  const pavColor = s.pavilion === 'Pabellón Capilar' ? '#0B8043' : '#D93025';
  const isMine = s.doctor_id === doctorId;
  return (
    <div
      data-testid={`surgery-card-${s.id}`}
      className={`bg-white rounded-xl border p-4 transition-shadow hover:shadow-sm ${
        isMine ? 'border-[#2B4C3B]/20' : 'border-[#E8E5DD]'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold font-mono text-[#2B4C3B]">{s.time}</span>
          <span className="text-sm text-[#6A736B] font-mono">– {s.end_time}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: pavColor }} />
          <span className="text-xs text-[#6A736B]">
            {s.pavilion === 'Pabellón Capilar' ? 'Capilar' : 'Estética'}
          </span>
        </div>
      </div>
      <h3 className="text-base font-semibold text-gray-900">{s.patient_name}</h3>
      <p className="text-sm text-[#6A736B]">{s.surgery_type}</p>
      <p className="text-xs text-[#6A736B]/70 font-mono mt-0.5">{s.patient_rut}</p>
      {showAll && s.doctor && (
        <p className="text-xs text-[#6A736B] mt-1">
          {s.doctor} {isMine && <span className="text-[#2B4C3B] font-semibold">(tú)</span>}
        </p>
      )}
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <StateBadge name={s.state_name} color={s.state_color} />
        <PayBadge estado={s.atencion_estado_pago} />
        {s.started_at && !s.ended_at && (
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 animate-pulse" data-testid="agenda-in-progress">
            EN CURSO · {formatChileHHmm(s.started_at)}
          </span>
        )}
        {s.ended_at && (
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-200 text-gray-700" data-testid="agenda-finished">
            FINALIZADA · {formatChileHHmm(s.ended_at)}
          </span>
        )}
      </div>
      {s.allergies && (
        <p className="text-xs text-red-700 mt-2 pt-2 border-t border-red-100 font-semibold" data-testid="agenda-allergies">
          ⚠ Alergias: <span className="font-normal">{s.allergies}</span>
        </p>
      )}
      {s.observations && (
        <p className="text-xs text-[#6A736B] mt-2 pt-2 border-t border-[#E8E5DD]" data-testid="agenda-observations">
          {s.observations}
        </p>
      )}
    </div>
  );
};

export function DoctorAgenda() {
  const { doctorId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(searchParams.get('ver') === 'todas');
  const [selectedDate, setSelectedDate] = useState(searchParams.get('fecha') || moment().format('YYYY-MM-DD'));
  const [viewMode, setViewMode] = useState(searchParams.get('vista') || 'dia');

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [doctorId, selectedDate, showAll, viewMode]);

  const updateParams = (overrides = {}) => {
    const params = {
      fecha: overrides.fecha || selectedDate,
      ver: overrides.ver !== undefined ? overrides.ver : (showAll ? 'todas' : 'mis'),
      vista: overrides.vista || viewMode,
    };
    setSearchParams(params);
  };

  const fetchData = async () => {
    try {
      const ver = showAll ? 'todas' : 'mis';
      const { data: res } = await axios.get(
        `${API_URL}/api/tv/agenda/${doctorId}?fecha=${selectedDate}&ver=${ver}&vista=${viewMode}`
      );
      setData(res);
    } catch (err) {
      console.error('Error fetching agenda:', err);
    } finally {
      setLoading(false);
    }
  };

  const navigate = (direction) => {
    const days = viewMode === 'semana' ? 7 : 1;
    const newDate = moment(selectedDate).add(direction * days, 'days').format('YYYY-MM-DD');
    setSelectedDate(newDate);
    updateParams({ fecha: newDate });
  };

  const goToToday = () => {
    const today = moment().format('YYYY-MM-DD');
    setSelectedDate(today);
    updateParams({ fecha: today });
  };

  const toggleFilter = () => {
    const newShowAll = !showAll;
    setShowAll(newShowAll);
    updateParams({ ver: newShowAll ? 'todas' : 'mis' });
  };

  const switchView = (newView) => {
    setViewMode(newView);
    updateParams({ vista: newView });
  };

  const isToday = selectedDate === moment().format('YYYY-MM-DD');

  // Daily data
  const dailySurgeries = showAll ? (data?.all_surgeries || []) : (data?.surgeries || []);

  // Weekly data
  const weekDates = data?.dates || [];
  const weekSurgeries = showAll ? (data?.all_surgeries_by_date || {}) : (data?.surgeries_by_date || {});

  const getDateLabel = () => {
    if (viewMode === 'semana' && data?.week_start && data?.week_end) {
      const s = moment(data.week_start);
      const e = moment(data.week_end);
      if (s.month() === e.month()) {
        return `${s.format('D')} - ${e.format('D MMM YYYY')}`;
      }
      return `${s.format('D MMM')} - ${e.format('D MMM YYYY')}`;
    }
    return `${isToday ? 'Hoy ' : ''}${moment(selectedDate).format('ddd D MMM YYYY')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-3 border-[#2B4C3B] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (data?.error) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-lg text-gray-500">Doctor no encontrado</p>
          <p className="text-sm text-gray-400 mt-1">Verifica el enlace</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="doctor-agenda-page">
      {/* Header */}
      <div className="bg-white border-b border-[#E8E5DD] sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-[#2B4C3B]" data-testid="doctor-name">
                {data?.doctor_name}
              </h1>
              <p className="text-xs text-[#6A736B]">Agenda Quirúrgica</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-[#6A736B]">
                {data?.total_mine || 0} mía{(data?.total_mine || 0) !== 1 ? 's' : ''} · {data?.total_all || 0} total
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="max-w-2xl mx-auto px-4 py-3 space-y-3">
        {/* View mode toggle */}
        <div className="flex bg-white rounded-lg border border-[#E8E5DD] p-1">
          <button
            onClick={() => switchView('dia')}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'dia' ? 'bg-[#2B4C3B] text-white' : 'text-[#6A736B] hover:text-[#2B4C3B]'
            }`}
            data-testid="view-day-btn"
          >
            Día
          </button>
          <button
            onClick={() => switchView('semana')}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'semana' ? 'bg-[#2B4C3B] text-white' : 'text-[#6A736B] hover:text-[#2B4C3B]'
            }`}
            data-testid="view-week-btn"
          >
            Semana
          </button>
        </div>

        {/* Date nav */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-[#E8E5DD] text-[#2B4C3B] hover:bg-[#F3EBE1] transition-colors"
            data-testid="prev-btn"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <button
            onClick={goToToday}
            className="text-sm font-semibold text-[#2B4C3B] capitalize"
            data-testid="date-display"
          >
            {getDateLabel()}
          </button>
          <button
            onClick={() => navigate(1)}
            className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-[#E8E5DD] text-[#2B4C3B] hover:bg-[#F3EBE1] transition-colors"
            data-testid="next-btn"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        </div>

        {/* Filter toggle */}
        <div className="flex bg-white rounded-lg border border-[#E8E5DD] p-1">
          <button
            onClick={() => { if (showAll) toggleFilter(); }}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
              !showAll ? 'bg-[#2B4C3B] text-white' : 'text-[#6A736B] hover:text-[#2B4C3B]'
            }`}
            data-testid="filter-mine-btn"
          >
            Mis Cirugías ({data?.total_mine || 0})
          </button>
          <button
            onClick={() => { if (!showAll) toggleFilter(); }}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
              showAll ? 'bg-[#2B4C3B] text-white' : 'text-[#6A736B] hover:text-[#2B4C3B]'
            }`}
            data-testid="filter-all-btn"
          >
            Todas ({data?.total_all || 0})
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 pb-8">
        {viewMode === 'dia' ? (
          /* Daily View */
          dailySurgeries.length > 0 ? (
            <div className="space-y-3">
              {dailySurgeries.map((s) => (
                <SurgeryCard key={s.id} s={s} showAll={showAll} doctorId={doctorId} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-[#6A736B] text-base">
                {showAll ? 'No hay cirugías programadas' : 'No tienes cirugías programadas'}
              </p>
              <p className="text-sm text-[#6A736B]/60 mt-1">
                {moment(selectedDate).format('dddd D [de] MMMM, YYYY')}
              </p>
            </div>
          )
        ) : (
          /* Weekly View */
          <div className="space-y-4">
            {weekDates.map((dateStr) => {
              const daySurgeries = weekSurgeries[dateStr] || [];
              const dayMoment = moment(dateStr);
              const isDayToday = dateStr === moment().format('YYYY-MM-DD');
              return (
                <div key={dateStr} data-testid={`week-day-${dateStr}`}>
                  <div className={`flex items-center gap-2 mb-2 py-1 ${isDayToday ? '' : ''}`}>
                    <div className={`flex items-center gap-2 ${isDayToday ? 'bg-[#2B4C3B] text-white px-3 py-1 rounded-full' : ''}`}>
                      <span className={`text-sm font-semibold capitalize ${isDayToday ? 'text-white' : 'text-[#2B4C3B]'}`}>
                        {dayMoment.format('ddd D')}
                      </span>
                    </div>
                    <span className="text-xs text-[#6A736B] capitalize">{dayMoment.format('MMMM')}</span>
                    {daySurgeries.length > 0 && (
                      <span className="text-xs text-[#6A736B] ml-auto">{daySurgeries.length} cirugía{daySurgeries.length !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                  {daySurgeries.length > 0 ? (
                    <div className="space-y-2 ml-0">
                      {daySurgeries.map((s) => (
                        <SurgeryCard key={s.id} s={s} showAll={showAll} doctorId={doctorId} />
                      ))}
                    </div>
                  ) : (
                    <div className="py-3 text-center">
                      <p className="text-xs text-[#6A736B]/40">—</p>
                    </div>
                  )}
                </div>
              );
            })}
            {weekDates.length === 0 && (
              <div className="text-center py-16">
                <p className="text-[#6A736B] text-base">No hay datos para esta semana</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
